(() => {
  'use strict';

  const loader = document.getElementById('latrand-zipchat-cart-refresh-script');
  if (!loader || loader.dataset.initialised === 'true') return;
  loader.dataset.initialised = 'true';

  // This flag records an interaction, not a successful add. The widget owns the add.
  let hasZipchatAddInteraction = false;
  let interactionVersion = 0;
  let nativeCartVersion = 0;
  let inFlight = null;
  let observedDrawer = null;
  const cartUpdateEvent = 'maximize:cart:updated';

  const elementPath = (event) => event.composedPath().filter((node) => node && node.nodeType === 1);
  const isUsingCart = () => {
    const active = document.activeElement;
    // Do not discard a customer interaction or their place in the cart. The
    // native drawer initially focuses CloseCart; that control is rebound below.
    return !!(active && active.id !== 'CloseCart' && active.closest('#CartDrawer, #MainCart__Items'));
  };
  const hasPendingQuantityEdit = () => Array.from(document.querySelectorAll(
    '#CartDrawer input[id^="CartItemQuantity--"], #MainCart__Items input[id^="CartItemQuantity--"]'
  )).some((input) => input.value !== input.defaultValue);
  const cartIsBusy = () => !!(
    document.getElementById('CartDrawer')?.hasAttribute('loading') ||
    window.xCartHelper?.isUpdating || isUsingCart() || hasPendingQuantityEdit()
  );

  const getTargets = () => {
    const helper = window.xCartHelper;
    const drawer = document.getElementById('CartDrawer');
    const main = document.getElementById('MainCart__Items');
    const primary = window.Maximize?.isCartPage ? main : drawer;
    const sectionId = primary?.dataset.sectionId;
    if (!sectionId || typeof helper?.getSectionsToRender !== 'function') return [];
    const targets = helper.getSectionsToRender(sectionId).filter((target) =>
      target && target.id && target.id !== 'undefined' && document.querySelector(target.selector)
    );
    // The native helper chooses the main cart on cart pages. Keep an existing
    // header drawer current there as well without replacing the whole header.
    if (drawer?.dataset.sectionId && !targets.some((target) => target.selector === '#CartDrawer')) {
      targets.push({ id: drawer.dataset.sectionId, selector: '#CartDrawer' });
    }
    return targets;
  };

  const refreshNativeCart = () => {
    if (!hasZipchatAddInteraction || inFlight || cartIsBusy()) return;
    const targets = getTargets();
    const sectionIds = [...new Set(targets.map((target) => target.id))];
    if (!targets.length || sectionIds.length > 5 || typeof getSectionInnerHTML !== 'function') return;
    const requestInteraction = interactionVersion;
    const requestCart = nativeCartVersion;
    const oldCount = window.xCartHelper.currentItemCount;
    const url = new URL(window.location.href);
    url.hash = '';
    url.searchParams.set('sections', sectionIds.join(','));

    inFlight = (async () => {
      try {
        // GET only: preserve the current locale, page context and preview query.
        const response = await fetch(url.href, {
          method: 'GET', credentials: 'same-origin', cache: 'no-store',
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) return;
        const sections = await response.json();
        if (requestInteraction !== interactionVersion || requestCart !== nativeCartVersion || cartIsBusy()) return;
        const replacements = targets.map((target) => {
          const element = document.querySelector(target.selector);
          const source = sections && sections[target.id];
          const html = typeof source === 'string' ? getSectionInnerHTML(source, target.selector) : undefined;
          return { element, html, selector: target.selector };
        });
        // A 200 response can contain null sections. Do not partially blank the UI.
        if (replacements.some(({ element, html }) => !element || typeof html !== 'string')) return;
        const rebindDrawerFocus = document.activeElement?.id === 'CloseCart';
        replacements.forEach(({ element, html, selector }) => {
          const visible = selector === '#CartDrawer' && element.querySelector('#CartDrawer__Wrapper')?.classList.contains('is-visible');
          element.innerHTML = html;
          if (visible) element.querySelector('#CartDrawer__Wrapper')?.classList.add('is-visible');
        });
        if (rebindDrawerFocus && document.getElementById('CartDrawer')?.hasAttribute('open')) {
          const focusStore = window.Alpine?.store?.('xFocusElement');
          if (typeof focusStore?.trapFocus === 'function') focusStore.trapFocus('CartDrawer__Wrapper', 'CloseCart');
          else document.getElementById('CloseCart')?.focus({ preventScroll: true });
        }
        window.xCartHelper.updateCurrentCountItem?.();
        document.dispatchEvent(new CustomEvent(cartUpdateEvent, {
          bubbles: true,
          detail: { isItemCountChanged: oldCount !== window.xCartHelper.currentItemCount }
        }));
      } catch {
        // Preserve the existing cart and let the next customer open try again.
      } finally {
        inFlight = null;
      }
    })();
  };

  const drawerObserver = new MutationObserver(() => {
    if (observedDrawer?.hasAttribute('open')) refreshNativeCart();
  });
  const bindDrawer = () => {
    const drawer = document.getElementById('CartDrawer');
    if (drawer === observedDrawer) return;
    drawerObserver.disconnect();
    observedDrawer = drawer;
    if (drawer) drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['open'] });
  };

  document.addEventListener('click', (event) => {
    const path = elementPath(event);
    const button = path.find((element) => element.matches('.zipchat-atc-add'));
    if (button) {
      const host = document.getElementById('zipchat-shadow-host');
      const root = host?.shadowRoot;
      const overlay = root?.querySelector('#zipchat-search-overlay');
      if (root && button.getRootNode() === root && overlay?.contains(button) &&
          button.closest('.zipchat-search-atc[role="dialog"]') &&
          !button.hasAttribute('disabled') && button.getAttribute('aria-disabled') !== 'true' &&
          button.getAttribute('aria-busy') !== 'true') {
        hasZipchatAddInteraction = true;
        interactionVersion += 1;
        bindDrawer();
      }
      return;
    }
    if (path.some((element) => element.id === 'cart-icon')) {
      bindDrawer();
      refreshNativeCart();
    }
  }, true);
  document.addEventListener('keyup', (event) => {
    if (event.key === 'Enter' && elementPath(event).some((element) => element.id === 'cart-icon')) {
      bindDrawer();
      refreshNativeCart();
    }
  }, true);
  document.addEventListener('cart:minicart:open', () => {
    bindDrawer();
    refreshNativeCart();
  });
  document.addEventListener(cartUpdateEvent, () => { nativeCartVersion += 1; });
  const markNativeCartEdit = (event) => {
    const target = event.target;
    if (target?.nodeType === 1 && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName) &&
        target.closest('#CartDrawer, #MainCart__Items')) nativeCartVersion += 1;
  };
  document.addEventListener('input', markNativeCartEdit, true);
  document.addEventListener('change', markNativeCartEdit, true);
  document.addEventListener('shopify:section:load', bindDrawer);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bindDrawer, { once: true });
  else bindDrawer();
})();
